<style>

.dashboard_ports {
    padding: 24px;
}

.dashboard_ports_header {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.dashboard_ports_controls {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.dashboard_ports_select {
    flex: 0 1 280px;
    min-width: 220px;
}

.dashboard_ports_ranges {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.dashboard_chart_container {
    min-height: 440px;
    position: relative;
}

.dashboard_chart {
    height: 440px;
    width: 100%;
}

.dashboard_no_data {
    color: rgba(0, 0, 0, 0.6);
    left: 0;
    position: absolute;
    right: 0;
    text-align: center;
    top: 200px;
}

.br {
    color: rgba(109, 103, 164);
}

</style>

<template>
<div class="dashboard_ports">
    <div class="dashboard_ports_header">
        <h1 class="br">Port traffic</h1>
    </div>

    <div class="dashboard_ports_controls">
        <v-select
            v-model="selected_port"
            class="dashboard_ports_select"
            :items="port_options"
            item-text="label"
            item-value="value"
            label="Port"
            dense
            outlined
            hide-details
            :disabled="ports_loading || port_options.length === 0"
            :loading="ports_loading"
            @change="change_port"
        ></v-select>
        <v-select
            v-model="selected_resolution"
            class="dashboard_ports_select"
            :items="data_intervals"
            item-text="label"
            item-value="value"
            label="Data interval"
            dense
            outlined
            hide-details
            @change="change_resolution"
        ></v-select>
        <div class="dashboard_ports_ranges">
            <v-btn-toggle
                v-model="selected_range_ms"
                mandatory
                dense
                @change="change_range"
            >
                <v-btn
                    v-for="range in available_ranges"
                    :key="range.label"
                    :value="range.value"
                    small
                >
                    {{ range.label }}
                </v-btn>
            </v-btn-toggle>
            <v-btn
                class="ml-2"
                small
                :disabled="loading || selected_port === null"
                @click="load_traffic"
            >
                Refresh
            </v-btn>
        </div>
    </div>

    <v-card outlined>
        <v-card-title>
            Port {{ selected_port === null ? "-" : selected_port }} traffic
        </v-card-title>
        <v-card-subtitle v-if="host && selected_port !== null">
            Host {{ host }}, BR instance {{ instance }},
            port {{ selected_port }}
        </v-card-subtitle>
        <v-progress-linear
            v-if="loading || ports_loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-alert
            v-if="error_message"
            class="ma-4"
            type="error"
            outlined
        >
            {{ error_message }}
        </v-alert>
        <v-card-text>
            <div class="dashboard_chart_container">
                <div ref="traffic_chart" class="dashboard_chart"></div>
                <div
                    v-if="!loading && !ports_loading &&
                        !error_message && points_count === 0"
                    class="dashboard_no_data"
                >
                    {{ no_data_message }}
                </div>
            </div>
        </v-card-text>
    </v-card>

    <v-card class="mt-4" outlined>
        <v-card-title>
            Port {{ selected_port === null ? "-" : selected_port }}
            packets and errors
        </v-card-title>
        <v-progress-linear
            v-if="loading || ports_loading"
            indeterminate
            color="primary"
        ></v-progress-linear>
        <v-card-text>
            <div class="dashboard_chart_container">
                <div ref="packet_chart" class="dashboard_chart"></div>
                <div
                    v-if="!loading && !ports_loading &&
                        !error_message && points_count === 0"
                    class="dashboard_no_data"
                >
                    {{ no_data_message }}
                </div>
            </div>
        </v-card-text>
    </v-card>
</div>
</template>

<script>

import * as echarts from "echarts";
import api_client from "../services/api.js";


const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const PACKET_RATE_SERIES = [
    { name: "RX packets", field: "rx_pps" },
    { name: "TX packets", field: "tx_pps" },
    { name: "Missed", field: "missed_ps" },
    { name: "TX errors", field: "err_tx_ps" },
    { name: "RX errors", field: "err_rx_ps" },
    { name: "No mbuf", field: "no_mbuf_ps" },
];

const DATA_INTERVALS = [
    {
        label: "1 minute — last 2 weeks",
        value: "1m",
        default_range_ms: DAY_MS,
        ranges: [
            { label: "1 hour", value: HOUR_MS },
            { label: "24 hours", value: DAY_MS },
            { label: "7 days", value: 7 * DAY_MS },
            { label: "14 days", value: 14 * DAY_MS },
        ],
    },
    {
        label: "5 minutes — last 30 days",
        value: "5m",
        default_range_ms: 30 * DAY_MS,
        ranges: [
            { label: "24 hours", value: DAY_MS },
            { label: "7 days", value: 7 * DAY_MS },
            { label: "14 days", value: 14 * DAY_MS },
            { label: "30 days", value: 30 * DAY_MS },
        ],
    },
    {
        label: "1 hour — last year",
        value: "1h",
        default_range_ms: 365 * DAY_MS,
        ranges: [
            { label: "7 days", value: 7 * DAY_MS },
            { label: "30 days", value: 30 * DAY_MS },
            { label: "90 days", value: 90 * DAY_MS },
            { label: "1 year", value: 365 * DAY_MS },
        ],
    },
];


function format_number(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 100) {
        return value.toFixed(0);
    }
    if (absolute_value >= 10) {
        return value.toFixed(1);
    }
    return value.toFixed(2);
}


function traffic_unit(value) {
    const absolute_value = Math.abs(value);
    if (absolute_value >= 1e9) {
        return { divisor: 1e9, prefix: "G" };
    }
    if (absolute_value >= 1e6) {
        return { divisor: 1e6, prefix: "M" };
    }
    if (absolute_value >= 1e3) {
        return { divisor: 1e3, prefix: "K" };
    }
    return { divisor: 1, prefix: "" };
}


function format_axis_rate(value) {
    const unit = traffic_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}`.trim();
}


function format_tooltip_rate(value) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
    }
    const unit = traffic_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}bit/s`;
}


function format_tooltip_event_rate(value) {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
    }
    const unit = traffic_unit(value);
    return `${format_number(value / unit.divisor)} ${unit.prefix}events/s`;
}


function request_error_message(error) {
    const response = error.response;
    return response && response.data
        ? response.data.detail || "Dashboard request failed"
        : "Dashboard request failed";
}


export default {
    name: "dashboard_ports",

    data: () => ({
        data_intervals: DATA_INTERVALS,
        error_message: "",
        host: "",
        instance: "1",
        loading: false,
        port_options: [],
        ports_loading: false,
        points_count: 0,
        request_id: 0,
        selected_port: null,
        selected_range_ms: DAY_MS,
        selected_resolution: "1m",
        zoom_end: 100,
        zoom_start: 0,
    }),

    computed: {
        available_ranges() {
            return this.selected_interval.ranges;
        },

        no_data_message() {
            if (this.port_options.length === 0) {
                return "No ports are available in the metrics database.";
            }
            return "No traffic data is available for this time range.";
        },

        selected_interval() {
            return this.data_intervals.find(interval => (
                interval.value === this.selected_resolution
            )) || this.data_intervals[0];
        },
    },

    mounted() {
        this.traffic_chart = echarts.init(this.$refs.traffic_chart);
        this.packet_chart = echarts.init(this.$refs.packet_chart);
        this.chart_group = `dashboard_port_${this._uid}`;
        this.traffic_chart.group = this.chart_group;
        this.packet_chart.group = this.chart_group;
        echarts.connect(this.chart_group);
        this.traffic_chart.on("datazoom", this.remember_zoom);
        this.packet_chart.on("datazoom", this.remember_zoom);
        window.addEventListener("resize", this.resize_charts);
        this.load_ports().then(() => {
            if (this.selected_port !== null) {
                this.load_traffic();
            }
        });
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.resize_charts);
        if (this.chart_group) {
            echarts.disconnect(this.chart_group);
        }
        if (this.traffic_chart) {
            this.traffic_chart.off("datazoom", this.remember_zoom);
            this.traffic_chart.dispose();
            this.traffic_chart = null;
        }
        if (this.packet_chart) {
            this.packet_chart.off("datazoom", this.remember_zoom);
            this.packet_chart.dispose();
            this.packet_chart = null;
        }
    },

    methods: {
        change_port() {
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_traffic();
        },

        change_range() {
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_traffic();
        },

        change_resolution() {
            this.selected_range_ms = this.selected_interval.default_range_ms;
            this.zoom_start = 0;
            this.zoom_end = 100;
            this.load_traffic();
        },

        remember_zoom(event) {
            const data_zoom = event && event.batch
                ? event.batch[0]
                : event;

            if (data_zoom && Number.isFinite(data_zoom.start)) {
                this.zoom_start = data_zoom.start;
            }
            if (data_zoom && Number.isFinite(data_zoom.end)) {
                this.zoom_end = data_zoom.end;
            }
        },

        resize_charts() {
            if (this.traffic_chart) {
                this.traffic_chart.resize();
            }
            if (this.packet_chart) {
                this.packet_chart.resize();
            }
        },

        load_ports() {
            this.ports_loading = true;
            this.error_message = "";
            return api_client.run_get_query('/dashboard/ports')
            .then(response => {
                const data = response.data;
                const ports = Array.isArray(data.ports) ? data.ports : [];
                const port_values = ports.filter(port => (
                    typeof port === "string"
                ));

                this.host = data.host || "";
                this.instance = data.instance || "1";
                this.port_options = port_values.map(port => ({
                    label: `Port ${port}`,
                    value: port,
                }));

                if (!port_values.includes(this.selected_port)) {
                    this.selected_port = port_values.includes("0")
                        ? "0"
                        : port_values[0] || null;
                }
            })
            .catch(error => {
                this.error_message = request_error_message(error);
                this.port_options = [];
                this.selected_port = null;
                this.points_count = 0;
                this.render_charts([]);
            })
            .finally(() => {
                this.ports_loading = false;
            });
        },

        load_traffic() {
            if (this.selected_port === null) {
                return Promise.resolve();
            }

            const end_time = new Date();
            const start_time = new Date(
                end_time.getTime() - this.selected_range_ms
            );
            const request_id = ++this.request_id;

            this.loading = true;
            this.error_message = "";
            return api_client.run_get_query('/dashboard/port_traffic', {
                start: start_time.toISOString(),
                end: end_time.toISOString(),
                port: this.selected_port,
                resolution: this.selected_resolution,
            })
            .then(response => {
                if (request_id !== this.request_id) {
                    return;
                }
                const data = response.data;
                const points = Array.isArray(data.points) ? data.points : [];
                this.host = data.host || "";
                this.instance = data.instance || "1";
                this.points_count = points.length;
                this.render_charts(points);
            })
            .catch(error => {
                if (request_id !== this.request_id) {
                    return;
                }
                this.error_message = request_error_message(error);
                this.points_count = 0;
                this.render_charts([]);
            })
            .finally(() => {
                if (request_id === this.request_id) {
                    this.loading = false;
                }
            });
        },

        render_charts(points) {
            const rx_data = points.map(point => [
                point.time,
                point.rx_bps,
            ]);
            const tx_data = points.map(point => [
                point.time,
                point.tx_bps,
            ]);

            this.traffic_chart.setOption({
                animation: false,
                color: ["#2878b5", "#2a9d55"],
                tooltip: {
                    trigger: "axis",
                    formatter: parameters => {
                        if (!parameters.length) {
                            return "";
                        }
                        const timestamp = new Date(
                            parameters[0].value[0]
                        ).toLocaleString();
                        const values = parameters.map(parameter => (
                            `${parameter.marker}${parameter.seriesName}: ` +
                            format_tooltip_rate(parameter.value[1])
                        ));
                        return [timestamp].concat(values).join("<br>");
                    },
                },
                legend: {
                    bottom: 45,
                    data: ["RX", "TX"],
                },
                grid: {
                    bottom: 86,
                    containLabel: true,
                    left: 20,
                    right: 24,
                    top: 48,
                },
                xAxis: {
                    type: "time",
                    boundaryGap: false,
                    axisLabel: {
                        hideOverlap: true,
                    },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    name: "bit/s",
                    axisLabel: {
                        formatter: format_axis_rate,
                    },
                },
                dataZoom: [
                    {
                        id: "traffic_inside_zoom",
                        type: "inside",
                        filterMode: "none",
                        start: this.zoom_start,
                        end: this.zoom_end,
                        xAxisIndex: 0,
                    },
                    {
                        id: "traffic_slider_zoom",
                        type: "slider",
                        filterMode: "none",
                        start: this.zoom_start,
                        end: this.zoom_end,
                        xAxisIndex: 0,
                        bottom: 8,
                        height: 28,
                        realtime: true,
                        showDataShadow: true,
                        showDetail: true,
                        zoomLock: false,
                        left: 130,
                        right: 130,
                    },
                ],
                series: [
                    {
                        name: "RX",
                        type: "line",
                        data: rx_data,
                        showSymbol: false,
                        sampling: "lttb",
                        areaStyle: {
                            opacity: 0.12,
                        },
                    },
                    {
                        name: "TX",
                        type: "line",
                        data: tx_data,
                        showSymbol: false,
                        sampling: "lttb",
                        areaStyle: {
                            opacity: 0.12,
                        },
                    },
                ],
            }, true);

            const packet_series = PACKET_RATE_SERIES.map(series => ({
                name: series.name,
                type: "line",
                data: points.map(point => [
                    point.time,
                    point[series.field],
                ]),
                showSymbol: false,
                sampling: "lttb",
            }));

            this.packet_chart.setOption({
                animation: false,
                color: [
                    "#2878b5",
                    "#2a9d55",
                    "#f28e2b",
                    "#d62728",
                    "#9467bd",
                    "#8c564b",
                ],
                tooltip: {
                    trigger: "axis",
                    formatter: parameters => {
                        if (!parameters.length) {
                            return "";
                        }
                        const timestamp = new Date(
                            parameters[0].value[0]
                        ).toLocaleString();
                        const values = parameters.map(parameter => (
                            `${parameter.marker}${parameter.seriesName}: ` +
                            format_tooltip_event_rate(parameter.value[1])
                        ));
                        return [timestamp].concat(values).join("<br>");
                    },
                },
                legend: {
                    bottom: 45,
                    data: PACKET_RATE_SERIES.map(series => series.name),
                    type: "scroll",
                },
                grid: {
                    bottom: 86,
                    containLabel: true,
                    left: 20,
                    right: 24,
                    top: 48,
                },
                xAxis: {
                    type: "time",
                    boundaryGap: false,
                    axisLabel: {
                        hideOverlap: true,
                    },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    name: "events/s",
                    axisLabel: {
                        formatter: format_axis_rate,
                    },
                },
                dataZoom: [
                    {
                        id: "packet_inside_zoom",
                        type: "inside",
                        filterMode: "none",
                        start: this.zoom_start,
                        end: this.zoom_end,
                        xAxisIndex: 0,
                    },
                    {
                        id: "packet_slider_zoom",
                        type: "slider",
                        filterMode: "none",
                        start: this.zoom_start,
                        end: this.zoom_end,
                        xAxisIndex: 0,
                        bottom: 8,
                        height: 28,
                        realtime: true,
                        showDataShadow: true,
                        showDetail: true,
                        zoomLock: false,
                        left: 130,
                        right: 130,
                    },
                ],
                series: packet_series,
            }, true);
        },
    },
};

</script>
